import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    ExternalLink, CalendarIcon, ChevronRight, ChevronLeft, Send, ShieldCheck,
    Landmark, AlertTriangle, Pencil, Car, Camera, Sparkles, ChevronDown,
    Users, Save, Tent, Minus, Plus,
} from "lucide-react";

import api, { formatApiErrorDetail, formatMoney } from "@/lib/api";
import {
    computeQuote, isAllInclusiveWP, computeCarpa, recommendedCarpaWidth, staffIncluded, detectSeason,
    CATEGORIES, LOCATIONS, WP_LOCKED_CATEGORIES, BASE_INFRASTRUCTURE, FAQ_ITEMS,
    MAX_GUESTS, PRICING_DISCLAIMER, EVENT_TYPES,
} from "@/lib/pricing";
import { buildQuotePdf } from "@/lib/pdf";
import PriceSidebar from "@/components/PriceSidebar";
import ProviderMatrix from "@/components/ProviderMatrix";
import Logo from "@/components/Logo";

const MONTHS_ES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const STEPS = [
    { key: "contact", label: "Tu Evento", cta: "Comenzar Cotización" },
    { key: "space", label: "Aforo & Estructuras", cta: "Siguiente: Seleccionar Proveedores" },
    { key: "matrix", label: "Mix & Match", cta: "Siguiente: Servicios Adicionales" },
    { key: "extras", label: "Servicios Adicionales", cta: "Ver Resumen del Presupuesto" },
    { key: "summary", label: "Resumen", cta: "" },
];

const SESSION_KEY = "hn_session_quote_id";

function getOrCreateSessionId() {
    try {
        let id = localStorage.getItem(SESSION_KEY);
        if (!id) {
            id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            localStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch (_e) {
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
}

const DEFAULT_STATE = {
    contact_name: "",
    groom_name: "",
    bride_name: "",
    event_type: "Boda",
    email: "",
    phone_code: "+593",
    phone_number: "",
    guests: "",
    date_mode: "exact",
    exact_date: "",
    tentative_months: [],
    carpa_mode: "auto",
    carpa_m2: "",
    locations: ["capilla"],
    providers: {},
    extra_services: {},
    tech_services: [],
    channel: "ambos",
    accepted_terms: false,
    session_quote_id: null,
};

export default function Cotizador() {
    const [config, setConfig] = useState(null);
    const [blockedDates, setBlockedDates] = useState([]);
    const [stepIdx, setStepIdx] = useState(0);
    const [state, setState] = useState(() => ({ ...DEFAULT_STATE, session_quote_id: getOrCreateSessionId() }));
    const [submitting, setSubmitting] = useState(false);
    const [returnToSummary, setReturnToSummary] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/config");
                setConfig(data);
            } catch (_e) {
                toast.error("No se pudo cargar la configuración");
            }
            try {
                const { data: av } = await api.get("/availability");
                setBlockedDates(av.blocked || []);
            } catch (_e) { /* opcional */ }
        })();
    }, []);

    const guestsNum = Number(state.guests) || 0;
    const isMegaEvent = guestsNum >= 751;

    const breakdown = useMemo(() => config ? computeQuote(state, config) : { lines: [], total: 0 }, [state, config]);
    const wpAllInclusive = useMemo(() => config ? isAllInclusiveWP(state, config) : false, [state, config]);

    const set = useCallback((patch) => setState((s) => ({ ...s, ...patch })), []);
    const step = STEPS[stepIdx];

    const hasDate = state.date_mode === "exact" ? !!state.exact_date : state.tentative_months.length > 0;

    const canNext = () => {
        if (step.key === "contact") {
            return state.contact_name && state.email && state.phone_number && state.event_type && hasDate && state.accepted_terms;
        }
        if (step.key === "space") {
            if (isMegaEvent) return false;
            return guestsNum > 0 && guestsNum <= MAX_GUESTS && (state.locations || []).length > 0;
        }
        if (step.key === "matrix") {
            const withProviders = CATEGORIES.filter((c) => (config?.providers || []).some((p) => p.category === c.key && p.active !== false));
            if (wpAllInclusive) return !!state.providers.wedding_planner;
            return withProviders.every((c) => !!state.providers[c.key]);
        }
        return true;
    };

    const saveDraft = async () => {
        try {
            await api.post("/leads/draft", {
                name: state.contact_name,
                email: state.email,
                phone: `${state.phone_code} ${state.phone_number}`.trim(),
                event_type: state.event_type,
                event_date_text: state.date_mode === "exact" ? state.exact_date : state.tentative_months.map((m) => MONTHS_ES[m - 1]).join(", "),
                session_quote_id: state.session_quote_id,
            });
        } catch (_e) { /* silencioso: el borrador es best-effort */ }
    };

    const next = () => {
        if (!canNext()) { toast.error("Completa los campos requeridos"); return; }
        if (step.key === "contact") saveDraft();
        setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
    };
    const prev = () => setStepIdx((i) => Math.max(0, i - 1));
    const goToStep = (key, fromSummary = false) => {
        const idx = STEPS.findIndex((s) => s.key === key);
        if (idx >= 0) {
            setStepIdx(idx);
            if (fromSummary) setReturnToSummary(true);
        }
    };
    const backToSummary = () => {
        setReturnToSummary(false);
        setStepIdx(STEPS.length - 1);
        toast.success("Cambios guardados — montos recalculados");
    };

    const onLogoClick = (e) => {
        if (stepIdx > 0) {
            e.preventDefault();
            if (window.confirm("¿Deseas volver al inicio? Se perderán los datos no guardados.")) {
                setState({ ...DEFAULT_STATE, session_quote_id: getOrCreateSessionId() });
                setStepIdx(0);
                setReturnToSummary(false);
                window.scrollTo({ top: 0 });
            }
        }
    };

    const eventLabel = () => {
        if (state.event_type === "Boda" && state.bride_name && state.groom_name) {
            return `la boda de ${state.bride_name} y ${state.groom_name}`;
        }
        return `${(state.event_type || "evento").toLowerCase()} de ${state.contact_name}`;
    };

    const submitLead = async (channel, extra = {}) => {
        if (!config) return null;
        setSubmitting(true);
        try {
            const payload = {
                contact_name: state.contact_name,
                groom_name: state.groom_name || "",
                bride_name: state.bride_name || "",
                event_type: state.event_type,
                email: state.email || null,
                phone: state.phone_number ? `${state.phone_code} ${state.phone_number}`.trim() : null,
                guests: guestsNum,
                date_mode: state.date_mode,
                exact_date: state.exact_date || null,
                tentative_months: state.tentative_months,
                location: (state.locations || []).join(","),
                locations: state.locations || [],
                providers: state.providers,
                tech_services: [],
                extra_services: state.extra_services,
                carpa: breakdown.carpa || {},
                channel,
                breakdown,
                total: breakdown.total,
                season: breakdown.season || "baja",
                session_quote_id: state.session_quote_id,
                is_mega_event: isMegaEvent,
                accepted_terms: state.accepted_terms,
                ...extra,
            };
            const { data } = await api.post("/leads", payload);
            if (data.account_created) {
                toast.success("¡Cuenta creada! Ya puedes acceder a 'Mi cuenta' para gestionar tu cotización.");
            } else if (data.is_update) {
                toast.success("Cotización actualizada (mismo ID de sesión).");
            }
            return data;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
            return null;
        } finally {
            setSubmitting(false);
        }
    };

    const buildAndUploadPdf = async () => {
        try {
            const doc = await buildQuotePdf({ state, breakdown, config });
            const blob = doc.output("blob");
            const form = new FormData();
            form.append("file", blob, `cotizacion_${state.contact_name || "evento"}.pdf`);
            form.append("bride_name", state.bride_name || state.contact_name || "");
            form.append("groom_name", state.groom_name || "");
            form.append("session_quote_id", state.session_quote_id || "");
            form.append("email", state.email || "");
            const { data } = await api.post("/quote/upload-pdf", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        } catch (_e) {
            return null;
        }
    };

    const downloadPdf = async () => {
        const doc = await buildQuotePdf({ state, breakdown, config });
        doc.save(`cotizacion_${(state.contact_name || "evento").replaceAll(" ", "_")}.pdf`);
    };

    const waNumber = (config?.settings?.whatsapp_number || "593999999999").replace(/[^0-9]/g, "");
    const calendlyUrl = config?.settings?.calendly_url || "https://calendly.com/aperez-ajcodelabs/30min";

    const sendWhatsApp = async (accountPassword) => {
        const lead = await submitLead("whatsapp", accountPassword ? { create_account_password: accountPassword } : {});
        if (!lead) return;
        const upload = await buildAndUploadPdf();
        if (upload?.token && state.phone_number) {
            const fullPhone = `${state.phone_code || "+593"}${state.phone_number.replace(/\s+/g, "")}`;
            try {
                await api.post("/quote/send-whatsapp", {
                    to_number: fullPhone,
                    pdf_token: upload.token,
                    bride_name: state.bride_name || state.contact_name,
                    groom_name: state.groom_name || "",
                    total: breakdown.total,
                    session_quote_id: state.session_quote_id,
                });
            } catch (_e) { /* sandbox */ }
        }
        const linkLine = upload?.public_url ? `\n\n📎 Ver cotización PDF: ${upload.public_url}` : "";
        const msg = `Hola, soy ${state.contact_name}, acabamos de cotizar ${eventLabel()}. Deseamos agendar una visita. Total referencial: ${formatMoney(breakdown.total)}.${linkLine}`;
        window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`, "_blank");
        toast.success(upload?.public_url ? "Enlace del PDF listo en el mensaje de WhatsApp." : "Lead registrado. Abre WhatsApp para continuar.");
    };

    const sendEmail = async (accountPassword) => {
        const lead = await submitLead("email", accountPassword ? { create_account_password: accountPassword } : {});
        if (!lead) return;
        const upload = await buildAndUploadPdf();
        const subject = `Cotización ${state.event_type} — ${state.contact_name} — Hacienda Nápoles`;
        const linkLine = upload?.public_url ? `\n\nVer PDF: ${upload.public_url}` : "";
        const body = `Hola,\n\nAdjunto la cotización referencial de ${eventLabel()}.\nTotal: ${formatMoney(breakdown.total)}.\nFecha: ${state.exact_date || state.tentative_months.map((m) => MONTHS_ES[m - 1]).join(", ")}\nInvitados: ${guestsNum}${linkLine}\n\nGracias,\n${state.contact_name}`;
        window.location.href = `mailto:ventas@haciendanapoles.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        toast.success(upload?.public_url ? "Enlace del PDF incluido en el email." : "Lead registrado.");
    };

    const submitMegaLead = async () => {
        const lead = await submitLead("email", {});
        if (lead) toast.success("Registramos tu solicitud como Lead Prioritario. Un asesor te contactará.");
    };

    if (!config) {
        return <div className="min-h-screen flex items-center justify-center overline text-muted-foreground">Cargando Hacienda Nápoles…</div>;
    }

    return (
        <div className="min-h-screen">
            <Header onLogoClick={onLogoClick} />
            {stepIdx === 0 && <><Hero /><IntroGallery settings={config.settings || {}} /></>}

            <main className="max-w-[1400px] mx-auto px-6 md:px-12 pt-6 pb-24">
                <ProgressBar stepIdx={stepIdx} onJump={(i) => setStepIdx(i)} />

                <div className={stepIdx === 0 ? "mt-8" : "grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8"}>
                    <section className={stepIdx === 0 ? "max-w-3xl mx-auto" : "lg:col-span-8"} data-testid={`step-${step.key}`}>
                        {step.key === "contact" && <StepContact state={state} set={set} blockedDates={blockedDates} config={config} />}
                        {step.key === "space" && (
                            <StepSpace
                                state={state} set={set} config={config}
                                isMegaEvent={isMegaEvent} onSubmitMega={submitMegaLead}
                                submitting={submitting} breakdown={breakdown} waNumber={waNumber}
                            />
                        )}
                        {step.key === "matrix" && <StepMatrix state={state} set={set} config={config} wpAllInclusive={wpAllInclusive} />}
                        {step.key === "extras" && <StepExtras state={state} set={set} config={config} guests={guestsNum} />}
                        {step.key === "summary" && (
                            <StepSummary
                                state={state} breakdown={breakdown} config={config} submitting={submitting}
                                downloadPdf={downloadPdf} sendWhatsApp={sendWhatsApp} sendEmail={sendEmail}
                                setState={setState} goToStep={goToStep} wpAllInclusive={wpAllInclusive}
                                eventLabel={eventLabel} waNumber={waNumber} calendlyUrl={calendlyUrl}
                            />
                        )}

                        {!(step.key === "space" && isMegaEvent) && (
                            <nav className="flex justify-between items-center pt-10 mt-10 border-t border-border flex-wrap gap-3">
                                <Button variant="ghost" onClick={prev} disabled={stepIdx === 0} data-testid="btn-prev-step" className="rounded-none">
                                    <ChevronLeft className="h-4 w-4 mr-2" /> Atrás
                                </Button>
                                <div className="flex gap-3">
                                    {returnToSummary && stepIdx < STEPS.length - 1 && (
                                        <Button onClick={backToSummary} data-testid="btn-back-to-summary" className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90">
                                            <Save className="h-4 w-4 mr-2" /> Guardar y Volver al Resumen
                                        </Button>
                                    )}
                                    {stepIdx < STEPS.length - 1 && (
                                        <Button
                                            onClick={next}
                                            disabled={!canNext()}
                                            data-testid="btn-next-step"
                                            className="rounded-none bg-primary text-primary-foreground hover:bg-primary/85 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            {step.cta} <ChevronRight className="h-4 w-4 ml-2" />
                                        </Button>
                                    )}
                                </div>
                            </nav>
                        )}

                        {/* Cláusula legal obligatoria — visible y destacada en todas las láminas */}
                        <div className="mt-8 bg-accent/10 border-l-4 border-accent p-4 flex items-start gap-3" data-testid="legal-clause">
                            <AlertTriangle className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                            <p className="text-sm font-medium text-foreground/90 leading-relaxed">{PRICING_DISCLAIMER}</p>
                        </div>
                    </section>

                    {stepIdx > 0 && !isMegaEvent && (
                        <section className="lg:col-span-4">
                            <PriceSidebar breakdown={breakdown} state={state} config={config} />
                        </section>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}

function Header({ onLogoClick }) {
    return (
        <header className="sticky top-0 bg-background/85 backdrop-blur-md border-b border-border z-40" data-testid="public-header">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                <a href="/" onClick={onLogoClick} className="flex items-center gap-3" data-testid="header-logo-link">
                    <Logo variant="seal" size={42} />
                    <div className="flex items-baseline gap-3">
                        <span className="font-display text-xl leading-none font-semibold">Hacienda Nápoles</span>
                        <span className="overline text-muted-foreground hidden md:inline">Puembo · Ecuador</span>
                    </div>
                </a>
                <nav className="flex items-center gap-6 overline">
                    <a href="https://instagram.com/hacienda_napoles_eventos" target="_blank" rel="noreferrer" className="underline-editorial hidden md:inline">Instagram</a>
                    <Link to="/login" className="underline-editorial" data-testid="nav-login">Mi cuenta</Link>
                    <Link to="/admin/login" className="underline-editorial text-muted-foreground hidden md:inline" data-testid="nav-admin">Admin</Link>
                </nav>
            </div>
        </header>
    );
}

function Hero() {
    return (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-12 md:pt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 animate-fade-up">
            <div className="md:col-span-7">
                <div className="overline text-muted-foreground">Cotizador Mix & Match · Hacienda Nápoles</div>
                <h1 className="font-display text-[40px] md:text-[62px] leading-[1.08] mt-6">
                    Diseña la Experiencia de tu
                    <span className="font-script text-[1.35em] text-accent block leading-none my-1">Evento Soñado</span>
                </h1>
                <p className="mt-6 max-w-xl text-muted-foreground text-lg">
                    Planifica, personaliza y cotiza tu celebración en Hacienda Nápoles en tiempo real.
                </p>
            </div>
            <div className="md:col-span-5">
                <div className="aspect-[4/5] bg-secondary border border-border overflow-hidden">
                    <img src="/hero-napoles.png" alt="Hacienda Nápoles" className="w-full h-full object-cover" />
                </div>
            </div>
        </section>
    );
}

function ProgressBar({ stepIdx, onJump }) {
    return (
        <div className="mt-8 flex items-center gap-2 md:gap-6 overflow-x-auto" data-testid="progress-bar">
            {STEPS.map((s, i) => (
                <div key={s.key} className="flex items-center gap-3 shrink-0">
                    <button
                        type="button"
                        onClick={() => i <= stepIdx && onJump && onJump(i)}
                        disabled={i > stepIdx}
                        className={`h-7 w-7 flex items-center justify-center text-xs tabular border transition ${
                            i <= stepIdx
                                ? "bg-primary text-primary-foreground border-primary cursor-pointer hover:opacity-80"
                                : "border-border text-muted-foreground cursor-not-allowed"
                        }`}
                        data-testid={`progress-step-${s.key}`}
                    >
                        {i + 1}
                    </button>
                    <span className={`overline ${i === stepIdx ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</span>
                    {i < STEPS.length - 1 && <span className="w-8 h-px bg-border" />}
                </div>
            ))}
        </div>
    );
}

// ---------- Intro / contexto + galería ----------
const GALLERY = [
    { src: "/gallery/capilla.jpg", label: "Capilla" },
    { src: "/gallery/jardines.jpg", label: "Jardines" },
    { src: "/gallery/reservorio.jpg", label: "Reservorio & Isla" },
    { src: "/gallery/carpa.jpg", label: "Recepciones" },
];

function IntroGallery({ settings }) {
    const wa = (settings?.whatsapp_number || "593999999999").replace(/[^0-9]/g, "");
    return (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-14" data-testid="intro-gallery">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-5">
                    <div className="overline text-accent">Bienvenidos a la Hacienda</div>
                    <h2 className="font-display text-3xl md:text-4xl mt-2">Un escenario, mil celebraciones</h2>
                    <p className="text-muted-foreground mt-4 leading-relaxed">
                        Hacienda Nápoles es una locación privada en Puembo pensada para <strong className="text-foreground">bodas, graduaciones,
                        eventos corporativos y aniversarios</strong>. Capilla colonial, jardines entre eucaliptos, un reservorio con isla
                        exclusiva y estructuras modulares para hasta 750 invitados — todo con parqueadero privado, suite de novios
                        y personal de apoyo incluido.
                    </p>
                    <p className="text-muted-foreground mt-3 leading-relaxed text-sm">
                        Con este cotizador diseñas tu evento en minutos: combina proveedores curados, calcula tu carpa
                        automáticamente y recibe tu presupuesto referencial en PDF.
                    </p>
                    <div className="flex gap-4 mt-6 overline">
                        <a href="https://instagram.com/hacienda_napoles_eventos" target="_blank" rel="noreferrer" className="underline-editorial text-accent" data-testid="social-instagram">Instagram →</a>
                        <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="underline-editorial" data-testid="social-whatsapp">WhatsApp →</a>
                    </div>
                </div>
                <div className="md:col-span-7">
                    <div className="grid grid-cols-2 gap-3">
                        {GALLERY.map((g) => (
                            <figure key={g.label} className="relative aspect-[3/2] overflow-hidden border border-border group">
                                <img src={g.src} alt={g.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                                <figcaption className="absolute bottom-0 left-0 right-0 bg-primary/75 text-primary-foreground overline text-[10px] px-3 py-1.5">{g.label}</figcaption>
                            </figure>
                        ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2 italic">Fotos referenciales — galería oficial próximamente.</p>
                </div>
            </div>
        </section>
    );
}

// ---------- Lámina 1: Captación de Leads ----------
function StepContact({ state, set, blockedDates, config }) {
    const blockedSet = useMemo(
        () => new Set((blockedDates || []).filter((b) => b.status === "reservado" || b.status === "tentativo").map((b) => b.date)),
        [blockedDates]
    );
    const softBlockedSet = useMemo(
        () => new Set((blockedDates || []).filter((b) => b.status === "bloqueado" || b.status === "mantenimiento").map((b) => b.date)),
        [blockedDates]
    );
    const highMonths = config?.pricing?.high_months || [];
    const season = detectSeason(state, config?.pricing || {});
    const hasDate = state.date_mode === "exact" ? !!state.exact_date : state.tentative_months.length > 0;
    return (
        <div className="animate-fade-up">
            <div className="overline text-muted-foreground">Paso 01</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Diseña la Experiencia de tu Evento Soñado</h2>
            <p className="text-muted-foreground mt-3 max-w-xl text-lg">
                Planifica, personaliza y cotiza tu celebración en Hacienda Nápoles en tiempo real.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                <Field label="Nombres y Apellidos" required>
                    <Input
                        data-testid="input-contact-name"
                        value={state.contact_name}
                        onChange={(e) => set({ contact_name: e.target.value })}
                        placeholder="María Fernanda Salazar"
                        className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-14"
                    />
                </Field>
                <Field label="Correo electrónico" required>
                    <Input
                        data-testid="input-email"
                        type="email"
                        value={state.email}
                        onChange={(e) => set({ email: e.target.value })}
                        placeholder="hola@ejemplo.com"
                        className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-14"
                    />
                </Field>
                <Field label="Teléfono / WhatsApp" required>
                    <div className="grid grid-cols-[110px_1fr] gap-2">
                        <Input
                            data-testid="input-phone-code"
                            value={state.phone_code}
                            onChange={(e) => set({ phone_code: e.target.value })}
                            placeholder="+593"
                            className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-14 tabular text-center"
                        />
                        <Input
                            data-testid="input-phone"
                            value={state.phone_number}
                            onChange={(e) => set({ phone_number: e.target.value.replace(/[^0-9 ]/g, "") })}
                            placeholder="99 999 9999"
                            className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-14 tabular"
                        />
                    </div>
                </Field>
                <Field label="Tipo de Evento" required>
                    <select
                        data-testid="select-event-type"
                        value={state.event_type}
                        onChange={(e) => set({ event_type: e.target.value })}
                        className="w-full h-14 border border-border bg-white px-4 rounded-none focus:outline-none focus:border-foreground"
                    >
                        {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </Field>
                {state.event_type === "Boda" && (
                    <>
                        <Field label="Nombre de la Novia (opcional)">
                            <Input
                                data-testid="input-bride"
                                value={state.bride_name}
                                onChange={(e) => set({ bride_name: e.target.value })}
                                placeholder="Camila"
                                className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-14"
                            />
                        </Field>
                        <Field label="Nombre del Novio (opcional)">
                            <Input
                                data-testid="input-groom"
                                value={state.groom_name}
                                onChange={(e) => set({ groom_name: e.target.value })}
                                placeholder="Andrés"
                                className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-14"
                            />
                        </Field>
                    </>
                )}
            </div>

            {/* Fecha del Evento */}
            <div className="mt-10">
                <div className="overline mb-3">Fecha del Evento (tentativa o confirmada) <span className="text-accent">*</span></div>
                <div className="flex gap-2">
                    <ToggleBtn active={state.date_mode === "exact"} onClick={() => set({ date_mode: "exact" })} testid="mode-exact">Fecha confirmada</ToggleBtn>
                    <ToggleBtn active={state.date_mode === "months"} onClick={() => set({ date_mode: "months" })} testid="mode-months">Meses tentativos</ToggleBtn>
                </div>
                {state.date_mode === "exact" ? (
                    <div className="mt-4 max-w-sm">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" data-testid="btn-date-picker" className="rounded-none justify-start w-full text-left font-mono h-14">
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {state.exact_date || "Seleccionar fecha"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-white border border-border rounded-none" align="start">
                                <Calendar
                                    mode="single"
                                    selected={state.exact_date ? new Date(state.exact_date + "T12:00:00") : undefined}
                                    disabled={(d) => blockedSet.has(d.toISOString().slice(0, 10))}
                                    onSelect={(d) => { if (d) set({ exact_date: d.toISOString().slice(0, 10) }); }}
                                />
                            </PopoverContent>
                        </Popover>
                        {state.exact_date && softBlockedSet.has(state.exact_date) && (
                            <p className="text-xs text-accent border border-accent bg-accent/5 p-2 mt-2" data-testid="blocked-date-warning">
                                ⚠ Esta fecha está temporalmente bloqueada por administración. Puedes generar tu cotización con normalidad, pero la fecha no está disponible para reserva hasta nuevo aviso.
                            </p>
                        )}
                        {blockedSet.size > 0 && (
                            <p className="text-xs text-muted-foreground mt-2">Las fechas no disponibles aparecen deshabilitadas en el calendario.</p>
                        )}
                    </div>
                ) : (
                    <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-2">
                        {MONTHS_ES.map((m, i) => {
                            const n = i + 1;
                            const active = state.tentative_months.includes(n);
                            const isHigh = highMonths.includes(n);
                            return (
                                <button
                                    key={n}
                                    type="button"
                                    data-testid={`month-${n}`}
                                    onClick={() => {
                                        const arr = active ? state.tentative_months.filter((x) => x !== n) : [...state.tentative_months, n];
                                        set({ tentative_months: arr });
                                    }}
                                    className={`border p-3 text-xs text-left ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground bg-white"}`}
                                >
                                    <div className="font-display text-sm leading-none">{m}</div>
                                    <div className={`overline mt-1 text-[9px] ${active ? "text-primary-foreground/60" : isHigh ? "text-accent" : "text-eucalyptus"}`}>
                                        {isHigh ? "T. Alta" : "T. Baja"}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
                {hasDate && (
                    <div className={`mt-3 overline text-xs ${season === "alta" ? "text-accent" : "text-eucalyptus"}`} data-testid="season-banner">
                        Detectado: Temporada {season === "alta" ? `Alta (+${config?.pricing?.high_season_surcharge_pct || 0}% sobre tarifa base)` : "Baja"}
                    </div>
                )}
            </div>

            {/* Legal checkbox obligatorio */}
            <div className="mt-10 bg-white border border-border p-5 flex items-start gap-3" data-testid="legal-checkbox-wrap">
                <Checkbox
                    checked={state.accepted_terms}
                    onCheckedChange={(v) => set({ accepted_terms: !!v })}
                    className="mt-1 rounded-none"
                    data-testid="check-accepted-terms"
                />
                <div className="text-sm text-muted-foreground leading-relaxed">
                    <strong className="text-foreground">Acepto los términos legales</strong>: Hacienda Nápoles se declara exclusivamente como <em>locación</em>. No asume responsabilidad solidaria por los servicios de terceros y cada proveedor firma un contrato independiente con el cliente. Los precios mostrados son referenciales. La reserva de fecha solo se confirma con depósito y contrato.
                </div>
            </div>
        </div>
    );
}

// ---------- Láminas 2 y 3: Espacio, Aforo, Estructuras y Personal ----------
function StepSpace({ state, set, config, isMegaEvent, onSubmitMega, submitting, breakdown, waNumber }) {
    const guests = Number(state.guests) || 0;
    const pricing = config.pricing || {};
    const staff = guests > 0 ? staffIncluded(guests, pricing) : null;
    const recommended = guests > 0 ? recommendedCarpaWidth(guests, pricing) : null;
    const mode = state.carpa_mode || "auto";
    const activeWidth = mode === "auto" ? recommended : mode;
    const c20 = guests > 0 ? computeCarpa(guests, "20", pricing) : null;
    const c25 = guests > 0 ? computeCarpa(guests, "25", pricing) : null;

    const toggleLoc = (key) => {
        const cur = state.locations || [];
        set({ locations: cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key] });
    };

    return (
        <div className="animate-fade-up">
            <div className="overline text-muted-foreground">Paso 02</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Espacio, Aforo y Estructuras</h2>
            <p className="text-muted-foreground mt-3 max-w-xl text-lg">
                Tarifa base de {formatMoney(pricing.base_price || 4500)} hasta {pricing.base_guests_included || 200} invitados. Desde el invitado {(pricing.base_guests_included || 200) + 1} aplica la matriz escalonada por persona extra.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10">
                <Field label="Aforo total de invitados (1–750)" required>
                    <Input
                        data-testid="input-guests"
                        type="number"
                        min={1}
                        max={5000}
                        value={state.guests}
                        onChange={(e) => set({ guests: Number(e.target.value) })}
                        className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-16 text-3xl font-display tabular px-4"
                    />
                </Field>
                {staff && !isMegaEvent && (
                    <div className="bg-white border border-border p-5" data-testid="staff-included">
                        <div className="flex items-center gap-2 overline text-accent"><Users className="h-4 w-4" /> Personal operativo incluido</div>
                        <div className="font-display text-3xl mt-2 tabular">{staff} personas</div>
                        <p className="text-xs text-muted-foreground mt-1">Aseo + parqueadero incluidos en el costo base (1–450: 6 personas · 451–750: 8 personas).</p>
                    </div>
                )}
            </div>

            {isMegaEvent ? (
                <div className="mt-10 bg-accent/10 border-l-4 border-accent p-6 md:p-8" data-testid="mega-event-block">
                    <div className="flex items-start gap-4">
                        <AlertTriangle className="h-7 w-7 text-accent shrink-0" />
                        <div className="flex-1">
                            <h3 className="font-display text-2xl md:text-3xl text-accent">Cotización Especial para Mega Eventos</h3>
                            <p className="mt-3 text-base leading-relaxed">
                                Para mega eventos de este volumen (<strong>+750 invitados</strong>), por favor contacta directamente a nuestro equipo para una asesoría personalizada.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <Button onClick={onSubmitMega} disabled={submitting} data-testid="btn-submit-mega" className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Send className="h-4 w-4 mr-2" /> Solicitar cotización personalizada
                                </Button>
                                <a
                                    href={`https://wa.me/${waNumber}?text=${encodeURIComponent("Hola, tenemos un mega evento de más de 750 invitados y necesitamos cotización personalizada.")}`}
                                    target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-2 border border-accent text-accent px-5 py-2.5 text-sm"
                                    data-testid="btn-mega-whatsapp"
                                >
                                    Coordinar por WhatsApp <ExternalLink className="h-4 w-4" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* Estructuras para salón */}
                    {guests > 0 && (
                        <div className="mt-12" data-testid="carpa-section">
                            <div className="flex items-center gap-2 overline text-muted-foreground"><Tent className="h-4 w-4" /> Estructuras para salón</div>
                            <h3 className="font-display text-2xl md:text-3xl mt-2">Carpa modular (opcional)</h3>
                            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                                El sistema sugiere automáticamente la carpa que mejor responde a tu aforo (1 módulo por cada 50 invitados o fracción). También puedes ingresar tu propio metraje o continuar sin carpa.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                {[{ key: "20", calc: c20 }, { key: "25", calc: c25 }].map(({ key, calc }) => {
                                    const isActive = (mode === "auto" || mode === "20" || mode === "25") && activeWidth === key;
                                    const spec = pricing[`carpa_${key}`] || {};
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            data-testid={`carpa-${key}`}
                                            onClick={() => set({ carpa_mode: key })}
                                            disabled={!calc}
                                            className={`tier-card text-left bg-white border p-6 ${!calc ? "opacity-40 cursor-not-allowed" : isActive ? "border-primary bg-secondary" : "border-border hover:border-foreground"}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="overline text-muted-foreground">Carpa {key}m de ancho</span>
                                                {recommended === key && <span className="overline text-[10px] text-accent">★ Sugerida</span>}
                                            </div>
                                            {calc ? (
                                                <>
                                                    <div className="font-display text-3xl mt-2 tabular">{formatMoney(calc.price)}</div>
                                                    <div className="text-sm text-muted-foreground mt-1">
                                                        {calc.modules} módulo(s) de {key}×5m · {calc.area_m2} m² · ${spec.price_per_m2}/m²
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-2 tabular">+ Flete estimado: {formatMoney(calc.flete)}</div>
                                                </>
                                            ) : (
                                                <div className="text-sm text-muted-foreground mt-3">No disponible para este aforo</div>
                                            )}
                                        </button>
                                    );
                                })}

                                {/* Metraje manual */}
                                <div
                                    className={`tier-card text-left bg-white border p-6 ${mode === "manual" ? "border-primary bg-secondary" : "border-border"}`}
                                    data-testid="carpa-manual"
                                >
                                    <span className="overline text-muted-foreground">Metraje manual (m²)</span>
                                    <p className="text-xs text-muted-foreground mt-1">¿Ya sabes cuántos m² necesitas? Ingrésalos directamente.</p>
                                    <div className="flex items-center gap-3 mt-3">
                                        <Input
                                            type="number"
                                            min={0}
                                            value={state.carpa_m2}
                                            onFocus={() => set({ carpa_mode: "manual" })}
                                            onChange={(e) => set({ carpa_mode: "manual", carpa_m2: e.target.value })}
                                            placeholder="Ej: 350"
                                            className="rounded-none border border-border bg-white h-12 tabular w-32"
                                            data-testid="carpa-m2-input"
                                        />
                                        <span className="text-sm text-muted-foreground">m² × ${(pricing.carpa_20 || {}).price_per_m2 || 4}/m²</span>
                                    </div>
                                    {mode === "manual" && Number(state.carpa_m2) > 0 && (
                                        <div className="font-display text-2xl mt-3 tabular" data-testid="carpa-manual-price">
                                            {formatMoney(Number(state.carpa_m2) * ((pricing.carpa_20 || {}).price_per_m2 || 4))}
                                            <span className="text-xs text-muted-foreground font-sans ml-2">+ flete {formatMoney((pricing.carpa_20 || {}).flete || 0)}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Sin carpa */}
                                <button
                                    type="button"
                                    data-testid="carpa-none"
                                    onClick={() => set({ carpa_mode: mode === "none" ? "auto" : "none" })}
                                    className={`tier-card text-left bg-white border p-6 ${mode === "none" ? "border-primary bg-secondary" : "border-border hover:border-foreground"}`}
                                >
                                    <span className="overline text-muted-foreground">Continuar sin carpa</span>
                                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                        La carpa no es obligatoria. Puedes cotizarla más adelante con nuestro equipo o con tu Wedding Planner.
                                    </p>
                                    {mode === "none" && <div className="mt-3 overline text-[10px] text-accent">✓ Sin carpa en esta cotización</div>}
                                </button>
                            </div>
                            {mode !== "auto" && mode !== "none" && (
                                <button type="button" onClick={() => set({ carpa_mode: "auto" })} className="mt-3 overline text-[11px] text-accent underline-editorial" data-testid="carpa-reset-auto">
                                    ↺ Volver a la sugerencia automática
                                </button>
                            )}
                        </div>
                    )}

                    {/* Escenarios */}
                    <div className="mt-12">
                        <h3 className="font-display text-2xl md:text-3xl">Escenarios del evento</h3>
                        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                            Puedes combinar varios espacios. La capilla viene incluida por defecto (puedes desmarcarla). <strong>La Isla del Reservorio está incluida en todos los paquetes</strong> sin costo adicional.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            {LOCATIONS.map((loc) => {
                                const selected = (state.locations || []).includes(loc.key);
                                return (
                                    <button
                                        key={loc.key}
                                        type="button"
                                        data-testid={`loc-${loc.key}`}
                                        onClick={() => toggleLoc(loc.key)}
                                        className={`tier-card text-left bg-white border p-6 ${selected ? "border-primary bg-secondary" : "border-border hover:border-foreground"}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="overline text-muted-foreground">Incluido</span>
                                            <span className={`h-5 w-5 border ${selected ? "bg-primary border-primary" : "border-border"} flex items-center justify-center`}>
                                                {selected && <span className="text-primary-foreground text-xs">✓</span>}
                                            </span>
                                        </div>
                                        <h4 className="font-serif italic text-2xl mt-2 leading-tight">{loc.label}</h4>
                                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{loc.description}</p>
                                    </button>
                                );
                            })}
                            <div className="tier-card text-left bg-secondary/60 border border-accent/40 p-6" data-testid="loc-isla-included">
                                <div className="flex items-center justify-between">
                                    <span className="overline text-accent">Incluida en todos los paquetes</span>
                                    <Landmark className="h-4 w-4 text-accent" />
                                </div>
                                <h4 className="font-serif italic text-2xl mt-2 leading-tight">La Isla del Reservorio</h4>
                                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                    Acceso exclusivo a la isla del reservorio para ceremonia, brindis o sesión fotográfica — sin costo adicional.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Infraestructura base incluida */}
                    <div className="mt-12 bg-white border border-border p-6 md:p-8" data-testid="base-infrastructure">
                        <div className="overline text-accent">Cortesía Hacienda Nápoles</div>
                        <h3 className="font-display text-2xl md:text-3xl mt-2">Infraestructura base incluida</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            {BASE_INFRASTRUCTURE.map((item) => (
                                <div key={item.label} className="flex items-start gap-3 bg-muted p-4">
                                    <div className="text-accent shrink-0 mt-0.5">
                                        {item.icon === "car" ? <Car className="h-5 w-5" /> :
                                         item.icon === "camera" ? <Camera className="h-5 w-5" /> :
                                         item.icon === "shield" ? <ShieldCheck className="h-5 w-5" /> :
                                         <Sparkles className="h-5 w-5" />}
                                    </div>
                                    <div>
                                        <div className="font-serif italic text-base">{item.label}</div>
                                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{item.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// ---------- Lámina 4: Mix & Match ----------
function StepMatrix({ state, set, config, wpAllInclusive }) {
    const onSelect = (categoryKey, providerId) => {
        set({ providers: { ...state.providers, [categoryKey]: providerId } });
    };

    useEffect(() => {
        if (wpAllInclusive) {
            const wpOnly = { wedding_planner: state.providers.wedding_planner };
            const hasOthers = Object.keys(state.providers).some((k) => k !== "wedding_planner" && state.providers[k]);
            if (hasOthers) set({ providers: wpOnly });
        }
    }, [wpAllInclusive, state.providers, set]);

    return (
        <div className="animate-fade-up">
            <div className="overline text-muted-foreground">Paso 03</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Selección de Proveedores</h2>

            {/* Banner destacado informativo */}
            <div className="mt-6 bg-white border border-border p-6" data-testid="mixmatch-banner">
                <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-accent shrink-0 mt-1" />
                    <div>
                        <strong className="text-foreground">Personaliza tu evento con Mix & Match.</strong>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                            Nuestro sistema Mix & Match te da la libertad de armar tu evento a tu gusto. Puedes combinar a tus proveedores preferidos en cada categoría (Catering, Decoración, Música, etc.), adaptando cada detalle a tu estilo y presupuesto.
                        </p>
                    </div>
                </div>
                <div className="flex items-start gap-3 mt-4 pt-4 border-t border-border">
                    <span className="text-lg leading-none">💡</span>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        <strong className="text-accent">¿Prefieres despreocuparte de la coordinación?</strong> Si eliges el paquete <strong>Todo Incluido</strong> de una Wedding Planner, ella asumirá la gestión integral de estos servicios automáticamente.
                    </p>
                </div>
            </div>

            {wpAllInclusive && (
                <div className="my-6 bg-accent/10 border-l-2 border-accent p-4 text-sm" data-testid="wp-all-inclusive-notice">
                    <strong className="text-accent">Wedding Planner &quot;Todo Incluido&quot; seleccionado.</strong> Las categorías Catering, Decoración, Música, Fotografía, Video y Estructura quedan cubiertas bajo el contrato de la WP.
                </div>
            )}

            <div className="mt-8">
                {CATEGORIES.map((c) => {
                    const locked = wpAllInclusive && WP_LOCKED_CATEGORIES.includes(c.key);
                    return (
                        <ProviderMatrix
                            key={c.key}
                            categoryKey={c.key}
                            categoryLabel={c.label}
                            providers={config.providers}
                            selectedId={state.providers[c.key]}
                            onSelect={onSelect}
                            guests={Number(state.guests) || 0}
                            disabled={locked}
                            disabledReason={locked ? "🔒 Este servicio está cubierto por la Wedding Planner seleccionada." : ""}
                        />
                    );
                })}
            </div>
        </div>
    );
}

// ---------- Lámina 5: Servicios Adicionales ----------
function StepExtras({ state, set, config, guests }) {
    const services = (config.extra_services || []).filter((s) => s.active !== false);

    const update = (id, patch) => {
        const cur = state.extra_services[id] || { on: false, qty: 1, option: 0 };
        set({ extra_services: { ...state.extra_services, [id]: { ...cur, ...patch } } });
    };

    return (
        <div className="animate-fade-up">
            <div className="overline text-muted-foreground">Paso 04</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Servicios Adicionales</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mb-10 text-lg">
                Complementa tu evento con la infraestructura, seguridad y experiencias que tus invitados merecen.
            </p>

            <div className="space-y-4">
                {services.map((s) => {
                    const sel = state.extra_services[s.id] || { on: false, qty: 1, option: 0 };
                    return (
                        <div key={s.id} className="bg-white border border-border p-5" data-testid={`extra-${s.id}`}>
                            <div className="flex items-start gap-4">
                                <Checkbox
                                    checked={!!sel.on}
                                    onCheckedChange={(v) => update(s.id, { on: !!v })}
                                    data-testid={`extra-check-${s.id}`}
                                    className="mt-1 rounded-none"
                                />
                                <div className="flex-1">
                                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                                        <h4 className="font-serif italic text-xl">{s.name}</h4>
                                        <span className="tabular text-base font-medium">
                                            {s.type === "selector" && `${formatMoney((s.options?.[sel.option ?? 0] || s.options?.[0])?.price || 0)}`}
                                            {s.type === "counter" && `${formatMoney(s.unit_price)} / ${s.unit_label}`}
                                            {s.type === "switch" && formatMoney(s.price)}
                                            {s.type === "per_guest" && `${formatMoney(s.per_guest_price)} / pax${guests > 0 ? ` ≈ ${formatMoney(s.per_guest_price * guests)}` : ""}`}
                                        </span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.description}</p>
                                    {s.provider_ref && (
                                        <p className="text-xs text-accent mt-1 overline" data-testid={`extra-provider-${s.id}`}>Proveedor de referencia: {s.provider_ref}</p>
                                    )}

                                    {sel.on && s.type === "selector" && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {(s.options || []).map((opt, i) => (
                                                <button
                                                    key={opt.label}
                                                    type="button"
                                                    data-testid={`extra-opt-${s.id}-${i}`}
                                                    onClick={() => update(s.id, { option: i })}
                                                    className={`px-4 py-2 border text-xs ${((sel.option ?? 0) === i) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground bg-white"}`}
                                                >
                                                    {opt.label} · {formatMoney(opt.price)}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {sel.on && s.type === "counter" && (
                                        <div className="flex items-center gap-3 mt-3">
                                            <button type="button" data-testid={`extra-minus-${s.id}`} onClick={() => update(s.id, { qty: Math.max(1, (Number(sel.qty) || 1) - 1) })} className="h-9 w-9 border border-border flex items-center justify-center hover:border-foreground bg-white">
                                                <Minus className="h-4 w-4" />
                                            </button>
                                            <span className="tabular text-lg w-16 text-center" data-testid={`extra-qty-${s.id}`}>{sel.qty || 1} <span className="text-xs text-muted-foreground">{s.unit_label}</span></span>
                                            <button type="button" data-testid={`extra-plus-${s.id}`} onClick={() => update(s.id, { qty: (Number(sel.qty) || 1) + 1 })} className="h-9 w-9 border border-border flex items-center justify-center hover:border-foreground bg-white">
                                                <Plus className="h-4 w-4" />
                                            </button>
                                            <span className="tabular text-sm text-muted-foreground ml-2">= {formatMoney((Number(sel.qty) || 1) * s.unit_price)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ---------- Láminas 6 y 7: Resumen ----------
function StepSummary({ state, breakdown, config, submitting, downloadPdf, sendWhatsApp, sendEmail, setState, goToStep, wpAllInclusive, eventLabel, waNumber, calendlyUrl }) {
    const [accountPassword, setAccountPassword] = useState("");
    const [savedConfigs, setSavedConfigs] = useState([]);
    const [savingConfig, setSavingConfig] = useState(false);
    const [showCompare, setShowCompare] = useState(false);
    const [compareWithId, setCompareWithId] = useState(null);

    const providerLabel = (catKey) => {
        const p = config.providers.find((x) => x.id === state.providers[catKey]);
        return p ? p.name : "—";
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/me/configurations");
                setSavedConfigs(data || []);
            } catch (_e) { /* no logueado */ }
        })();
    }, []);

    const saveConfiguration = async () => {
        const name = window.prompt("Nombra esta configuración (ej: 'Opción A — Premium')");
        if (!name) return;
        setSavingConfig(true);
        try {
            const { data } = await api.post("/me/configurations", {
                name,
                session_quote_id: state.session_quote_id,
                guests: Number(state.guests) || 0,
                date_mode: state.date_mode,
                exact_date: state.exact_date,
                tentative_months: state.tentative_months,
                locations: state.locations,
                providers: state.providers,
                tech_services: [],
                breakdown,
                total: breakdown.total,
            });
            setSavedConfigs((prev) => [data, ...prev]);
            toast.success(`Configuración "${name}" guardada.`);
        } catch (e) {
            toast.error(e.response?.status === 401
                ? "Inicia sesión (crea una cuenta abajo con tu email) para guardar configuraciones."
                : "No se pudo guardar la configuración.");
        } finally {
            setSavingConfig(false);
        }
    };

    const compareConfig = savedConfigs.find((c) => c.id === compareWithId);
    const title = state.event_type === "Boda" && state.bride_name && state.groom_name
        ? <>{state.bride_name} <span className="text-muted-foreground">&</span> {state.groom_name}</>
        : <>{state.event_type} — {state.contact_name || "—"}</>;

    return (
        <div className="animate-fade-up" data-testid="summary">
            <div className="overline text-muted-foreground">Paso 05</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Resumen de tu Presupuesto</h2>
            <p className="text-muted-foreground mt-3 max-w-xl text-lg">
                Revisa el desglose de los servicios seleccionados. Puedes modificar cualquier sección antes de generar tu documento.
            </p>

            <div className="mt-8 bg-white border border-border p-8">
                <div className="font-serif italic text-3xl">{title}</div>
                <Separator className="my-6" />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
                    <Meta label="Tipo de evento" value={state.event_type} onEdit={() => goToStep("contact", true)} />
                    <Meta label="Invitados" value={state.guests} onEdit={() => goToStep("space", true)} />
                    <Meta label="Escenarios" value={(state.locations || []).map((k) => k.replaceAll("_", " ")).join(", ") || "—"} onEdit={() => goToStep("space", true)} />
                    <Meta label="Fecha" value={state.date_mode === "exact" ? state.exact_date : state.tentative_months.map((m) => MONTHS_ES[m - 1]).join(", ") || "—"} onEdit={() => goToStep("contact", true)} />
                </div>

                <Separator className="my-6" />

                {/* Desglose colapsable — precio único global al frente */}
                <details className="text-sm" data-testid="summary-lines">
                    <summary className="cursor-pointer overline text-accent select-none">Ver detalle del presupuesto ▾</summary>
                    <div className="space-y-2 mt-3">
                        {(breakdown.lines || []).map((l) => (
                            <div key={l.key} className="flex justify-between items-baseline gap-4">
                                <span className="text-muted-foreground">{l.label}</span>
                                <span className="tabular whitespace-nowrap">{formatMoney((l.amount || 0) + (l.surcharge || 0))}</span>
                            </div>
                        ))}
                        {breakdown.carpa_included_in_wp && (
                            <div className="flex justify-between items-baseline gap-4">
                                <span className="text-muted-foreground">Estructura / Carpa</span>
                                <span className="italic text-accent text-xs">incluida en WP Todo Incluido</span>
                            </div>
                        )}
                    </div>
                </details>

                <Separator className="my-6" />

                <div className="space-y-2 text-base">
                    {CATEGORIES.map((c) => {
                        const isLocked = wpAllInclusive && WP_LOCKED_CATEGORIES.includes(c.key);
                        return (
                            <div key={c.key} className="flex justify-between items-center">
                                <span className="text-muted-foreground">{c.label}</span>
                                <span className="flex items-center gap-3">
                                    <span className={isLocked ? "italic text-accent text-sm" : ""}>
                                        {isLocked ? "incluido en WP" : providerLabel(c.key)}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => goToStep("matrix", true)}
                                        className="text-[11px] overline text-accent underline-editorial"
                                        data-testid={`edit-${c.key}`}
                                    >
                                        <Pencil className="inline h-3 w-3 mr-1" /> Editar
                                    </button>
                                </span>
                            </div>
                        );
                    })}
                    <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Servicios Adicionales</span>
                        <button type="button" onClick={() => goToStep("extras", true)} className="text-[11px] overline text-accent underline-editorial" data-testid="edit-extras">
                            <Pencil className="inline h-3 w-3 mr-1" /> Editar
                        </button>
                    </div>
                </div>

                <Separator className="my-6" />

                <div className="flex items-baseline justify-between">
                    <span className="overline">Total referencial</span>
                    <span className="font-display text-4xl md:text-5xl tabular tabular-lg" data-testid="summary-total">{formatMoney(breakdown.total)}</span>
                </div>

                <p className="mt-4 text-xs italic text-muted-foreground border-l-2 border-muted-foreground/40 pl-3 leading-relaxed">
                    {PRICING_DISCLAIMER}
                </p>
            </div>

            {/* Módulo comparativo */}
            <div className="mt-10 bg-white border border-border p-6 md:p-8" data-testid="compare-module">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="overline text-accent">Módulo comparativo</div>
                        <h3 className="font-display text-2xl md:text-3xl mt-1">Compara opciones</h3>
                    </div>
                    <Button onClick={saveConfiguration} disabled={savingConfig} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/85" data-testid="btn-save-config">
                        <Save className="h-4 w-4 mr-2" /> Guardar esta configuración
                    </Button>
                </div>
                {savedConfigs.length > 0 && (
                    <div className="mt-6">
                        <div className="overline text-muted-foreground mb-3">Comparar con:</div>
                        <div className="flex flex-wrap gap-2">
                            {savedConfigs.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => { setCompareWithId(c.id); setShowCompare(true); }}
                                    className={`px-3 py-2 text-xs border ${compareWithId === c.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground"}`}
                                    data-testid={`compare-with-${c.id}`}
                                >
                                    {c.name} · {formatMoney(c.total)}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {showCompare && compareConfig && (
                    <CompareTable current={{ state, breakdown, name: "Actual" }} other={compareConfig} config={config} />
                )}
            </div>

            {/* Crea tu cuenta */}
            {state.email && (
                <div className="mt-10 bg-secondary border border-border p-6 md:p-8" data-testid="account-section">
                    <div className="overline text-accent">Mi cuenta</div>
                    <h3 className="font-display text-2xl md:text-3xl mt-2">Guarda tu cotización</h3>
                    <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                        Crea una cuenta con tu email <strong>{state.email}</strong> para acceder a esta y futuras cotizaciones.
                    </p>
                    <div className="mt-6 max-w-xl">
                        <Input
                            type="password"
                            value={accountPassword}
                            onChange={(e) => setAccountPassword(e.target.value)}
                            placeholder="Crea una contraseña (mín. 6 caracteres)"
                            className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-14"
                            data-testid="input-account-password"
                        />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                        Al presionar &quot;Enviar por WhatsApp&quot; o &quot;Enviar por Email&quot; abajo, tu cuenta se crea automáticamente.
                    </p>
                </div>
            )}

            {/* Acciones */}
            <div className="mt-10">
                <div className="overline text-muted-foreground mb-3">Canal de envío del PDF</div>
                <div className="flex flex-wrap gap-2">
                    {[{ key: "whatsapp", label: "WhatsApp" }, { key: "email", label: "Email" }, { key: "ambos", label: "Ambos" }].map((c) => (
                        <ToggleBtn key={c.key} active={state.channel === c.key} onClick={() => setState((s) => ({ ...s, channel: c.key }))} testid={`channel-${c.key}`}>
                            {c.label}
                        </ToggleBtn>
                    ))}
                </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
                <Button onClick={downloadPdf} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/85" data-testid="btn-download-pdf">
                    📄 Descargar Cotización en PDF
                </Button>
                {(state.channel === "whatsapp" || state.channel === "ambos") && (
                    <Button onClick={() => sendWhatsApp(accountPassword)} disabled={submitting} className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90" data-testid="btn-send-whatsapp">
                        <Send className="h-4 w-4 mr-2" /> Consultar por WhatsApp
                    </Button>
                )}
                {(state.channel === "email" || state.channel === "ambos") && (
                    <Button onClick={() => sendEmail(accountPassword)} disabled={submitting} variant="outline" className="rounded-none border-foreground" data-testid="btn-send-email">
                        <Send className="h-4 w-4 mr-2" /> Enviar por Correo
                    </Button>
                )}
            </div>

            <div className="mt-12 p-8 bg-primary text-primary-foreground">
                <div className="overline text-primary-foreground/70">Siguiente paso</div>
                <h3 className="font-display text-2xl md:text-3xl mt-2">Coordina tu visita técnica</h3>
                <p className="text-sm mt-2 opacity-80 max-w-xl">La reserva solo se confirma con depósito y contrato. Agenda una reunión o visita con nuestro equipo.</p>
                <div className="flex flex-wrap gap-3 mt-6">
                    <a href={calendlyUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-5 py-3" data-testid="btn-calendly">
                        Agendar reunión por Calendly <ExternalLink className="h-4 w-4" />
                    </a>
                    <a
                        href={`https://wa.me/${waNumber}?text=${encodeURIComponent(`Hola, soy ${state.contact_name || "—"}, acabamos de cotizar ${eventLabel()}. Deseamos agendar una visita.`)}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground px-5 py-3 border border-primary-foreground/30"
                        data-testid="btn-coordinate-visit"
                    >
                        Coordinar visita por WhatsApp <ExternalLink className="h-4 w-4" />
                    </a>
                </div>
            </div>

            {/* Beneficios y alianzas */}
            <div className="mt-14" data-testid="benefits-section">
                <div className="overline text-muted-foreground">Cortesía Hacienda Nápoles</div>
                <h3 className="font-display text-2xl md:text-3xl mt-2">Beneficios y alianzas</h3>
                <div className="mt-4 mb-6 bg-accent/10 border-l-2 border-accent p-4 text-sm" data-testid="courtesy-code-notice">
                    <strong className="text-accent">Código de cortesía</strong> — El código exclusivo para hacer efectivos estos descuentos se libera <em>únicamente cuando el administrador registre el abono de reserva de fecha</em>.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {config.benefits.map((b) => (
                        <a
                            key={b.id}
                            href={b.external_url || "#"}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => { if (!b.external_url) e.preventDefault(); }}
                            className="tier-card bg-white border border-border p-6 hover:border-foreground group cursor-pointer"
                            data-testid={`benefit-${b.partner_name}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="overline text-accent">{b.category}</div>
                                {b.external_url && <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition" />}
                            </div>
                            <h4 className="font-serif italic text-xl mt-2">{b.partner_name}</h4>
                            <p className="text-sm text-muted-foreground mt-2">{b.description}</p>
                            <div className="mt-4 overline text-accent">→ {b.discount_text}</div>
                        </a>
                    ))}
                </div>
            </div>

            {/* FAQ */}
            <div className="mt-14" data-testid="faq-section">
                <div className="overline text-muted-foreground">Preguntas frecuentes</div>
                <h3 className="font-display text-2xl md:text-3xl mt-2">FAQ</h3>
                <div className="mt-6 space-y-2">
                    {(config.faqs?.length ? config.faqs : FAQ_ITEMS).map((item, i) => <FAQItem key={item.q} q={item.q} a={item.a} idx={i} />)}
                </div>
            </div>

            <div className="mt-14" data-testid="legal-notes">
                <div className="overline text-muted-foreground mb-3">Notas obligatorias</div>
                <ol className="text-xs text-muted-foreground space-y-2 list-decimal pl-4">
                    {config.legal_notes.map((n) => <li key={n.slice(0, 32)}>{n}</li>)}
                </ol>
            </div>
        </div>
    );
}

function FAQItem({ q, a, idx }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="bg-white border border-border" data-testid={`faq-item-${idx}`}>
            <button type="button" className="w-full flex items-center justify-between p-4 text-left" onClick={() => setOpen((v) => !v)}>
                <span className="font-serif italic text-lg">{q}</span>
                <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">{a}</div>}
        </div>
    );
}

function CompareTable({ current, other, config }) {
    const byId = Object.fromEntries((config.providers || []).map((p) => [p.id, p]));
    const rows = CATEGORIES.map((c) => ({
        label: c.label,
        currP: byId[current.state.providers?.[c.key]],
        otherP: byId[other.providers?.[c.key]],
    }));

    return (
        <div className="mt-8 overflow-x-auto" data-testid="compare-table">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="border-b-2 border-foreground">
                        <th className="text-left py-3 overline text-muted-foreground">Rubro</th>
                        <th className="text-left py-3 font-serif italic">{current.name}</th>
                        <th className="text-left py-3 font-serif italic">{other.name}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className="border-b border-border">
                        <td className="py-3 text-muted-foreground">Invitados</td>
                        <td className="py-3 tabular">{current.state.guests}</td>
                        <td className="py-3 tabular">{other.guests}</td>
                    </tr>
                    {rows.map((r) => (
                        <tr key={r.label} className="border-b border-border">
                            <td className="py-3 text-muted-foreground">{r.label}</td>
                            <td className="py-3">{r.currP ? r.currP.name : <span className="text-muted-foreground/60">—</span>}</td>
                            <td className="py-3">{r.otherP ? r.otherP.name : <span className="text-muted-foreground/60">—</span>}</td>
                        </tr>
                    ))}
                    <tr className="border-t-2 border-foreground bg-secondary">
                        <td className="py-4 overline">Total referencial</td>
                        <td className="py-4 font-display tabular text-2xl" data-testid="compare-total-curr">{formatMoney(current.breakdown.total)}</td>
                        <td className="py-4 font-display tabular text-2xl" data-testid="compare-total-other">{formatMoney(other.total)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function Meta({ label, value, onEdit }) {
    return (
        <div>
            <div className="overline text-muted-foreground">{label}</div>
            <div className="mt-1 font-serif italic text-lg capitalize">{value}</div>
            {onEdit && (
                <button type="button" onClick={onEdit} className="mt-1 overline text-[10px] text-accent underline-editorial" data-testid={`edit-meta-${label}`}>
                    <Pencil className="inline h-3 w-3 mr-1" /> Editar
                </button>
            )}
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="flex flex-col gap-2">
            <div>{children}</div>
            <Label className="overline text-muted-foreground whitespace-nowrap">
                {label}{required && <span className="text-accent ml-1">*</span>}
            </Label>
        </div>
    );
}

function ToggleBtn({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`px-4 py-2 border text-xs overline ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground bg-white"}`}
        >
            {children}
        </button>
    );
}

function Footer() {
    return (
        <footer className="border-t border-border mt-24 py-12">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="flex items-start gap-4">
                    <Logo variant="seal" size={56} />
                    <div>
                        <div className="font-display text-xl font-semibold">Hacienda Nápoles</div>
                        <div className="overline text-muted-foreground mt-1">Puembo · Ecuador</div>
                    </div>
                </div>
                <div className="text-xs text-muted-foreground italic">
                    {PRICING_DISCLAIMER}
                </div>
                <div className="text-xs text-muted-foreground">
                    © {new Date().getFullYear()} — Hacienda Nápoles. Todos los derechos reservados.
                </div>
            </div>
        </footer>
    );
}
